import { useCallback, useEffect, useRef, useState } from 'react';
import { Paper, Typography } from '@mui/material';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import { getAccessAnalyzerConfig } from '../../../services/accessAnalyzerApi';
import AccessAnalyzerSetup from './AccessAnalyzerSetup';
import AccessAnalyzerDashboard from './AccessAnalyzerDashboard';
import OperationProgress from './OperationProgress';
import { POLL_INTERVAL_MS } from './pollInterval';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading, PortalErrorState, PortalSectionHeader } from '../../../components/portal/PortalStates';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import { CK, FONT_BODY, sectionCardSx } from '../portalStyles';

// Must match `color` in engines/accessanalyzer.js - the sidebar, the header avatar
// and the wizard's buttons all claim to be "the Access Analyzer raspberry".
const ACCESS_ANALYZER_COLOR = '#AD1457';

// Consecutive background-poll failures tolerated before the error card replaces
// the progress view. Matches the dashboard's counter, so at the shared 3s cadence
// a hard fault surfaces after roughly nine seconds.
const MAX_POLL_FAILURES = 3;

// Every Access Analyzer state (loading, error, transitioning) keeps the same page
// header mounted so the layout never jumps as the config lands.
const accessAnalyzerHeader = (
  <PortalPageHeader
    icon={<VpnKeyOutlinedIcon sx={{ fontSize: 24 }} />}
    title="IAM Access Analyzer"
    subtitle="External access and unused access across your accounts"
    color={ACCESS_ANALYZER_COLOR}
  />
);

// transitionCopy picks the in-progress wording from the config's progressLabel.
//
// progressLabel is the ONLY reliable discriminator between the fan-outs. There is
// no "disabling" status: DisableAccessAnalyzer flips the row to in_progress and
// stamps "Disabling" in the same UPDATE, and the update paths stamp "Updating" the
// same way - both atomic with the status change. errorMessage (which the
// GuardDuty/Macie entries key off) is useless here: the disable path never writes
// it and the add-member/retry paths clear it.
//
// Enable is the fallback rather than a matched case because it is the one path
// that can briefly show a stale or empty label: RetryMember and AddMembers set the
// status before their goroutine gets around to stamping "Granting permissions" /
// "Creating analyzers".
function transitionCopy(progressLabel) {
  const hint = (progressLabel || '').toLowerCase();
  if (hint.includes('disabl')) {
    return {
      title: 'Disabling IAM Access Analyzer…',
      body: 'Deleting the analyzers Prism created in every enrolled account and region and detaching the scoped role permissions Prism attached. Analyzers that already existed when Prism adopted them are left running - deleting one would destroy the findings they hold, which are yours. This can take a few minutes.',
    };
  }
  if (hint.includes('updat')) {
    return {
      title: 'Updating IAM Access Analyzer…',
      body: 'Applying the requested regions and unused-access setting across every enrolled account. This can take a few minutes.',
    };
  }
  return {
    title: 'Enabling IAM Access Analyzer across accounts and regions…',
    body: 'Granting scoped permissions and creating an analyzer in each account. Accounts are enrolled independently, so results appear as each one finishes.',
  };
}

// AccessAnalyzerEntry decides what to render at /securityhub/accessanalyzer. Flow:
//   no config / disabled / failed  → setup wizard
//   in_progress                    → transitioning view (polls until done)
//   enabled / partial_failure      → Access Analyzer dashboard
//
// partial_failure deliberately routes to the dashboard, NOT back to the wizard. In
// the flat peer model every account is enrolled on its own and one account's
// failure never blocks the rest, so partial_failure means "most of this is live and
// analyzing" - the per-member Retry and the Fix-roles flow on the Enrollment page
// are the repair path, and re-running the wizard would tear the working accounts
// down and start over.
//
// The "in_progress" branch covers every fan-out that can be started from elsewhere
// in the UI - initial enable, add/retry member, a region or unused-access change,
// and teardown from Settings → Danger zone - because all of them route here and let
// this component's polling drive the page forward.
export default function AccessAnalyzerEntry() {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  const pollFailuresRef = useRef(0);

  const refresh = useCallback(async ({ showSpinner = true } = {}) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await getAccessAnalyzerConfig();
      // A tenant that has never enabled Access Analyzer resolves with data: null.
      // GET /config answers that state with HTTP 200 and a null body - the handler
      // says so verbatim - so the wizard signal arrives here as a RESOLVED NULL,
      // exactly as it does for the five sibling engines. It is not a rejection and
      // must never render an error banner.
      setConfig(res?.data || null);
      pollFailuresRef.current = 0;
      setError(null);
    } catch (e) {
      // Defensive arm for the ONE divergence documented in accessAnalyzerApi.js,
      // which describes the un-enabled state as a 404 carrying the
      // ErrAccessAnalyzerNotConfigured sentinel. The handler in this build answers
      // 200/null instead, so this branch is dead today - but if the endpoint ever
      // moved to the sentinel, treating it as a fault would show every un-enabled
      // tenant a red error card where the setup wizard belongs, which is the single
      // worst failure this page has. portalFetch discards the status code, so the
      // message is the only thing there is to match on.
      if (String(e?.message || '').toLowerCase().includes('not configured')) {
        setConfig(null);
        pollFailuresRef.current = 0;
        setError(null);
      } else if (showSpinner) {
        // The error card is checked BEFORE the in_progress branch, so reporting
        // every rejection would let one blipped poll replace a multi-minute enable
        // or teardown narration with a red failure - during exactly the operation
        // this screen exists to narrate. A user-visible load has nothing to fall
        // back on and reports at once; a background poll has to fail repeatedly
        // first. Staying silent forever is the opposite lie (a dead backend would
        // render as a progress bar that is still "working"), so a sustained outage
        // does eventually surface, and the next successful tick clears it.
        setError(e.message);
      } else {
        pollFailuresRef.current += 1;
        if (pollFailuresRef.current >= MAX_POLL_FAILURES) setError(e.message);
      }
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll during any in_progress transition so the view advances by itself once the
  // orchestrator goroutine finishes. Depending on the status alone (not the whole
  // config) keeps the interval from being torn down and rebuilt on every tick,
  // which would reset the cadence each time the progress percentage moves.
  useEffect(() => {
    if (config?.status !== 'in_progress') return undefined;
    // Each transition starts with a clean slate - failures tolerated during an
    // earlier operation must not count against this one.
    pollFailuresRef.current = 0;
    pollRef.current = setInterval(() => refresh({ showSpinner: false }), POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [config?.status, refresh]);

  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{accessAnalyzerHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading IAM Access Analyzer…" color={ACCESS_ANALYZER_COLOR} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{accessAnalyzerHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={() => refresh()} />
        </motion.div>
      </motion.div>
    );
  }

  if (config?.status === 'in_progress') {
    const { title, body } = transitionCopy(config.progressLabel);
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{accessAnalyzerHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalSectionHeader
              icon={<VpnKeyOutlinedIcon sx={{ fontSize: 20 }} />}
              title={title}
              color={ACCESS_ANALYZER_COLOR}
              mb={1.5}
            />
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mb: 2 }}>
              {body}
            </Typography>
            <OperationProgress percent={config.progress} label={config.progressLabel || 'Working…'} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  // Terminal "nothing is enrolled" states - surface the setup wizard so the user
  // can re-run. EnableAccessAnalyzer drops the settled prior config and its children
  // server-side before creating a new one, so submitting again is safe.
  if (!config || config.status === 'disabled' || config.status === 'failed') {
    return (
      <AccessAnalyzerSetup
        onComplete={refresh}
        priorFailure={config?.status === 'failed' ? config.errorMessage : null}
      />
    );
  }

  return <AccessAnalyzerDashboard config={config} onConfigChange={refresh} />;
}
